import type {
  MediaItemInterface,
  MediaImageInterface
} from '@hypercodestudio/basler-components';

import { fetchContentfulAssetUrl } from '~/utils/contentful/fetchContentfulAssetUrl';
import { generateImageObject } from '~/utils/generateImageObject';
import { lightingAdvisorSetupSketchOptions } from '~/utils/imagePresetRegistry';
import { buildLightingAdvisorOptionImages } from '~/utils/lightingAdvisor/buildLightingAdvisorOptionImages';
import type { LightingAdvisorConfiguration } from '~/utils/lightingAdvisor/configurationSchema';

const OVERLAY_IMAGE_SIZE = { width: 1380, height: 1035 } as const;

export function useLightingAdvisorImages(
  configuration: Ref<LightingAdvisorConfiguration>
) {
  const { $activeLocale } = useNuxtApp();
  const optionImages = ref<Partial<Record<string, MediaImageInterface>>>({});
  const overlayImages = ref<Partial<Record<string, MediaItemInterface>>>({});

  async function resolveAssets(
    assets: Readonly<Record<string, string | undefined>>
  ): Promise<Record<string, string | null>> {
    const entries = Object.entries(assets).filter(
      (entry): entry is [string, string] => Boolean(entry[1])
    );
    const resolved = await Promise.all(
      entries.map(async ([key, assetId]) => [
        key,
        await fetchContentfulAssetUrl(
          assetId,
          $activeLocale.value.contentfulCode
        )
      ])
    );
    return Object.fromEntries(resolved);
  }

  async function refresh(): Promise<void> {
    const images = configuration.value.images;
    const [optionUrls, overlayUrls] = await Promise.all([
      resolveAssets(images?.options ?? {}),
      resolveAssets(images?.overlays ?? {})
    ]);

    optionImages.value = buildLightingAdvisorOptionImages(optionUrls);
    overlayImages.value = Object.fromEntries(
      Object.entries(overlayUrls).flatMap(([questionId, url]) => {
        if (!url) {
          return [];
        }
        return [
          [
            questionId,
            generateImageObject(
              { url, ...OVERLAY_IMAGE_SIZE },
              lightingAdvisorSetupSketchOptions
            )
          ]
        ];
      })
    );
  }

  watch(
    () => [
      configuration.value.images?.options,
      configuration.value.images?.overlays,
      $activeLocale.value.contentfulCode
    ],
    () => {
      void refresh();
    },
    { immediate: true, deep: true }
  );

  return { optionImages, overlayImages };
}
